/**
 * Renders the sidebar for the players page, containing a list of teammates using the relations API
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ImageContainer from "../core/ImageContainer";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Loading from "../core/Loading";
import ProfileImageContainer from "../core/ProfileImageContainer";

const processRelationsData = (jsonData) => {
  return jsonData;
};

const TeammatesSidebar = ({ gradientStyle }) => {
  const { playerId } = useParams();

  // Fetch relations data from API
  const { isLoading, error, data } = useQuery({
    queryKey: ["player", playerId, "relations"],
    queryFn: async () => {
      const response = await fetch(
        `${process.env.REACT_APP_BUIHA_API_URL}/player/${playerId}/relations/`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const json = await response.json();
      return processRelationsData(json); // Assuming this is your data processing function
    },
    staleTime: 0, // Set the staleTime (adjust as needed)
    enabled: !!playerId, // Only fetch if playerId exists
  });

  return (
    <div className="sidebar w-full flex flex-col items-center justify-start w-auto xs:mx-1 xs:mb-4 xl:mr-8 xl:px-0 px-2 xxs:px-4 xs:px-8 mb-2">
      <>
        <h2 className="w-full text-xl font-normal text-center">Teammates</h2>

        <div className="w-full h-2 mt-2 mb-4" style={gradientStyle}></div>

        <div className="max-xl:max-w-[300px] flex flex-col w-full items-start justify-start">
          {isLoading && (
            <>
              <div className="flex flex-col justify-center items-center m-1 mx-auto">
                <Loading />
              </div>
            </>
          )}
          {error && <div>Error: {error.message}</div>}
          {/* For each teammate */}
          {data &&
            data.teammates.length > 0 &&
            data.teammates.map((player) => (
              <Link
                to={`/player/${player.id}`}
                className="w-full no-underline group"
              >
                <div className="flex flex-row max-lg:w-full mb-2 justify-start items-start">
                  <div className="rounded xs:min-w-16 min-h-12 h-12 w-12 xs:min-h-16 xs:h-16 xs:w-16 mr-3 rounded-full bg-light-1 dark:bg-dark-2 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out group-hover:scale-105">
                    <ProfileImageContainer
                        player={player.id}
                        club={player.primary_team.club}
                        backup={player.primary_team.logo}
                        alt={`${player.forename} ${player.surname}`}
                        extraClasses={"rounded-full"}
                    />
                  </div>

                  {/* Player name */}
                  <div className="flex flex-col justify-start items-start">
                    <p className="font-semibold transition-colors duration-300 ease-in-out group-hover:text-buiha-red dark:group-hover:text-dark-5">
                      {player.forename} {player.surname}
                    </p>
                    <p className="text-darken text-xs">
                      {player.games} Games
                    </p>
                  </div>
                </div>
              </Link>
            ))}
        </div>
        </>

      <>
        <h2 className="w-full text-xl font-normal text-center">Opponents</h2>

        <div className="w-full h-2 mt-2 mb-4" style={gradientStyle}></div>

        <div className="max-xl:max-w-[300px] flex flex-col w-full items-start justify-start">
          {isLoading && (
              <>
                <div className="flex flex-col justify-center items-center m-1 mx-auto">
                  <Loading />
                </div>
              </>
          )}
          {error && <div>Error: {error.message}</div>}
          {/* For each teammate */}
          {data &&
              data.opponents.length > 0 &&
              data.opponents.map((player) => (
                  <Link
                      to={`/player/${player.id}`}
                      className="w-full no-underline group"
                  >
                    <div className="flex flex-row max-lg:w-full mb-2 justify-start items-start">
                      <div className="rounded xs:min-w-16 min-h-12 h-12 w-12 xs:min-h-16 xs:h-16 xs:w-16 mr-3 rounded-full bg-light-1 dark:bg-dark-2 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out group-hover:scale-105">
                        <ProfileImageContainer
                            player={player.id}
                            club={player.primary_team.club}
                            backup={player.primary_team.logo}
                            alt={`${player.forename} ${player.surname}`}
                            extraClasses={"rounded-full"}
                        />
                      </div>

                      {/* Player name */}
                      <div className="flex flex-col justify-start items-start">
                        <p className="font-semibold transition-colors duration-300 ease-in-out group-hover:text-buiha-red dark:group-hover:text-dark-5">
                          {player.forename} {player.surname}
                        </p>
                        <p className="text-darken text-xs">
                          {player.games} Games
                        </p>
                      </div>
                    </div>
                  </Link>
              ))}
        </div>
      </>
    </div>
  );
};

export default TeammatesSidebar;
